import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Wifi, Clock, Award, ChevronRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { getVenueImage } from "../utils/defaultImages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FeaturedVenue() {
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/venues/featured`);
        setVenue(response.data);
      } catch (error) {
        console.error("Error fetching featured venue:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-amber-200/50 rounded w-40 mb-4"></div>
        <div className="flex gap-4">
          <div className="w-32 h-32 bg-amber-200/50 rounded-xl"></div>
          <div className="flex-1 space-y-3">
            <div className="h-5 bg-amber-200/50 rounded w-3/4"></div>
            <div className="h-4 bg-amber-200/50 rounded w-1/2"></div>
            <div className="h-4 bg-amber-200/50 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!venue) return null;

  const imageUrl = getVenueImage(venue, 400);
  const daysUntilChange = venue._featured_meta?.changes_in_days || 7;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden"
    >
      <Link
        to={`/venue/${venue.id}`}
        className="block bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 rounded-2xl p-5 border border-amber-200/50 hover:border-amber-300 hover:shadow-lg transition-all group"
        data-testid="featured-venue-link"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-amber-500 rounded-lg">
              <Award className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-amber-800">Featured Venue of the Week</span>
          </div>
          <span className="text-xs text-amber-600 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Changes in {daysUntilChange} day{daysUntilChange !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Content */}
        <div className="flex gap-4">
          {/* Image */}
          <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-xl overflow-hidden flex-shrink-0">
            <img
              src={imageUrl}
              alt={venue.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {/* Premium Badge */}
            <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded-full">
              <Sparkles className="w-3 h-3" />
              Premium
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-amber-700 transition-colors">
              {venue.name}
            </h3>
            
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <MapPin className="w-4 h-4 text-amber-500" />
              <span>{venue.neighborhood || venue.location?.city}</span>
              <span className="text-gray-300">•</span>
              <span className="capitalize">{venue.venue_type?.replace("_", " ")}</span>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded-lg">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                <span className="font-bold text-amber-700">{venue.rating?.toFixed(1)}</span>
              </div>
              <span className="text-sm text-gray-500">
                ({venue.review_count || 0} reviews)
              </span>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-lg text-xs text-gray-600 border border-gray-100">
                <Wifi className="w-3 h-3" />
                {venue.amenities?.wifi_quality || "WiFi"}
              </span>
              {venue.amenities?.good_for_calls && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs border border-green-100">
                  Good for Calls
                </span>
              )}
              {venue.amenities?.is_spacious && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs border border-purple-100">
                  Spacious
                </span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <div className="hidden sm:flex items-center">
            <ChevronRight className="w-6 h-6 text-amber-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
          </div>
        </div>

        {/* Description preview */}
        {venue.description && (
          <p className="mt-3 text-sm text-gray-600 line-clamp-2">
            {venue.description}
          </p>
        )}
      </Link>
    </motion.div>
  );
}
